import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Html5QrcodeScanner } from 'html5-qrcode';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { CheckCircle, Camera, Type, Package, ArrowLeft, QrCode, XCircle, AlertTriangle, Calendar } from 'lucide-react';
import { formatDateTime } from '../utils/dateFormat';

import { API } from '../lib/apiBase';

export default function CheckInPage() {
  const { beneficiaryId } = useParams();
  const navigate = useNavigate();
  const [mode, setMode] = useState(beneficiaryId ? 'confirm' : 'select');
  const [loading, setLoading] = useState(false);
  const [beneficiary, setBeneficiary] = useState(null);
  const [manualCode, setManualCode] = useState('');
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState('');
  const scannerRef = useRef(null);
  const [scannerInitialized, setScannerInitialized] = useState(false);

  useEffect(() => {
    if (beneficiaryId) {
      fetchBeneficiaryPublic(beneficiaryId);
    }
  }, [beneficiaryId]);

  useEffect(() => {
    if (mode === 'scan' && !scannerInitialized) {
      initScanner();
    }
    return () => {
      if (scannerRef.current) {
        scannerRef.current.clear().catch(() => {});
      }
    };
  }, [mode, scannerInitialized]);

  const fetchBeneficiaryPublic = async (id) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/beneficiaries/public/${id}`);
      setBeneficiary(response.data);
      setMode('confirm');
    } catch (error) {
      if (error.response?.status === 404) {
        setMode('invalid');
      } else {
        setBeneficiary({ id, first_name: 'Beneficiary', surname: '' });
        setMode('confirm');
      }
    } finally {
      setLoading(false);
    }
  };

  const initScanner = () => {
    const scanner = new Html5QrcodeScanner('qr-reader', {
      fps: 10,
      qrbox: { width: 250, height: 250 },
      aspectRatio: 1,
    });

    scanner.render(onScanSuccess, onScanError);
    scannerRef.current = scanner;
    setScannerInitialized(true);
  };

  const onScanSuccess = (decodedText) => {
    const match = decodedText.match(/check-in\/([a-f0-9-]+)/i);
    if (match) {
      const id = match[1];
      if (scannerRef.current) {
        scannerRef.current.clear().catch(() => {});
      }
      navigate(`/check-in/${id}`);
    } else {
      toast.error('Invalid QR code');
    }
  };

  const onScanError = (error) => {};

  const handleManualSubmit = () => {
    if (!manualCode.trim()) {
      toast.error('Please enter a code');
      return;
    }
    const match = manualCode.match(/check-in\/([a-f0-9-]+)/i);
    const id = match ? match[1] : manualCode.trim();
    navigate(`/check-in/${id}`);
  };

  const handleCheckIn = async () => {
    if (!beneficiary?.id) {
      toast.error('Beneficiary not found');
      return;
    }

    setLoading(true);
    try {
      const itemsList = items.split(',').map(i => i.trim()).filter(i => i);
      
      const response = await axios.post(`${API}/distributions/check-in-public/${beneficiary.id}`, {
        items_delivered: itemsList,
        notes: notes || null
      });

      setBeneficiary(prev => ({
        ...prev,
        first_name: response.data.beneficiary_name?.split(' ')[0] || prev?.first_name,
        surname: response.data.beneficiary_name?.split(' ').slice(1).join(' ') || prev?.surname
      }));
      
      setMode('success');
      if (response.data.duplicate_this_month) {
        toast.warning('Note: this beneficiary had already received a distribution this month.', { duration: 7000 });
      } else {
        toast.success('Check-in completed!');
      }
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('QR code no longer valid: this beneficiary has been removed.', { duration: 8000 });
        setMode('invalid');
      } else {
        toast.error(error.response?.data?.detail || 'Error during check-in');
      }
    } finally {
      setLoading(false);
    }
  };

  if (mode === 'select') {
    return (
      <div className="min-h-screen bg-[#FAFAF8] flex items-center justify-center p-4" data-testid="checkin-select-page">
        <Card className="w-full max-w-md border-[#E5E5E5]">
          <CardHeader className="text-center">
            <div className="w-16 h-16 rounded-full bg-[#E9EFEA] flex items-center justify-center mx-auto mb-4">
              <Package className="w-8 h-8 text-[#4A5D4E]" />
            </div>
            <CardTitle className="text-2xl font-['Manrope']">Distribution Check-in</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button
              className="w-full h-16 bg-[#4A5D4E] hover:bg-[#3D4D40] text-lg"
              onClick={() => setMode('scan')}
              data-testid="scan-qr-button"
            >
              <Camera className="w-6 h-6 mr-3" />
              Scan QR Code
            </Button>
            <Button
              variant="outline"
              className="w-full h-16 text-lg"
              onClick={() => setMode('manual')}
              data-testid="manual-code-button"
            >
              <Type className="w-6 h-6 mr-3" />
              Enter Code Manually
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (mode === 'scan') {
    return (
      <div className="min-h-screen bg-[#FAFAF8] flex flex-col items-center justify-center p-2 md:p-4" data-testid="checkin-scan-page">
        <Card className="w-full max-w-md border-[#E5E5E5]">
          <CardHeader className="pb-3">
            <Button
              variant="ghost"
              size="sm"
              className="absolute left-2 top-2 md:left-4 md:top-4"
              onClick={() => { setMode('select'); setScannerInitialized(false); }}
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back
            </Button>
            <CardTitle className="text-center text-lg md:text-xl font-['Manrope'] pt-6">Scan QR Code</CardTitle>
          </CardHeader>
          <CardContent className="p-2 md:p-6">
            <div id="qr-reader" className="w-full rounded-lg overflow-hidden [&_video]:rounded-lg" />
            <p className="text-center text-xs md:text-sm text-[#8C8C8C] mt-3 md:mt-4 px-2">
              Point camera at the beneficiary card QR code
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (mode === 'manual') {
    return (
      <div className="min-h-screen bg-[#FAFAF8] flex items-center justify-center p-4" data-testid="checkin-manual-page">
        <Card className="w-full max-w-md border-[#E5E5E5]">
          <CardHeader>
            <Button
              variant="ghost"
              size="sm"
              className="absolute left-4 top-4"
              onClick={() => setMode('select')}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <CardTitle className="text-center font-['Manrope'] pt-4">Enter Code</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Beneficiary Code or URL</Label>
              <Input
                value={manualCode}
                onChange={(e) => setManualCode(e.target.value)}
                placeholder="Paste the code or URL..."
                data-testid="manual-code-input"
              />
            </div>
            <Button
              className="w-full bg-[#4A5D4E] hover:bg-[#3D4D40]"
              onClick={handleManualSubmit}
              data-testid="submit-manual-code-button"
            >
              Continue
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (mode === 'confirm') {
    return (
      <div className="min-h-screen bg-[#FAFAF8] flex items-center justify-center p-4" data-testid="checkin-confirm-page">
        <Card className="w-full max-w-md border-[#E5E5E5]">
          <CardHeader className="text-center">
            <div className="w-16 h-16 rounded-full bg-[#E9EFEA] flex items-center justify-center mx-auto mb-4">
              <QrCode className="w-8 h-8 text-[#4A5D4E]" />
            </div>
            <CardTitle className="text-2xl font-['Manrope']">Confirm Distribution</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {loading ? (
              <div className="text-center py-8 text-[#8C8C8C]">Loading...</div>
            ) : (
              <>
                <div className="bg-[#F0EFEA] rounded-lg p-4 text-center">
                  <p className="text-xs md:text-sm text-[#8C8C8C]">Beneficiary</p>
                  <p className="text-lg md:text-xl font-semibold text-[#1A1A1A] font-['Manrope'] break-words">
                    {beneficiary?.first_name} {beneficiary?.surname}
                  </p>
                  {beneficiary?.child_first_name && (
                    <p className="text-xs md:text-sm text-[#595959] mt-1 break-words">
                      Child: <span className="font-medium">{beneficiary.child_first_name}</span>
                    </p>
                  )}
                  {beneficiary?.child_birthdate && (
                    <p className="text-xs md:text-sm text-[#595959] mt-0.5 flex items-center justify-center gap-1">
                      <Calendar className="w-3.5 h-3.5" />
                      Birthdate: <span className="font-medium">{beneficiary.child_birthdate}</span>
                    </p>
                  )}
                </div>

                {beneficiary?.already_checked_in_this_month && (
                  <div className="bg-[#FFF8E1] border border-[#FCD9A1] rounded-lg p-3 flex gap-2" data-testid="same-month-warning">
                    <AlertTriangle className="w-5 h-5 text-[#B45309] flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-[#92400E]">
                      <p className="font-semibold">Already checked in this month</p>
                      <p className="text-xs mt-0.5">
                        This beneficiary already received a distribution this month. You can proceed, but double-check that this is intentional.
                      </p>
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <Label>Items Delivered (optional)</Label>
                  <Input
                    value={items}
                    onChange={(e) => setItems(e.target.value)}
                    placeholder="e.g. Diapers, Milk, Clothes..."
                    data-testid="items-input"
                  />
                  <p className="text-xs text-[#8C8C8C]">Separate items with a comma</p>
                </div>

                <div className="space-y-2">
                  <Label>Notes (optional)</Label>
                  <Textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Any notes..."
                    rows={3}
                    data-testid="notes-input"
                  />
                </div>

                <Button
                  className="w-full h-14 bg-[#4A5D4E] hover:bg-[#3D4D40] text-lg"
                  onClick={handleCheckIn}
                  disabled={loading}
                  data-testid="confirm-checkin-button"
                >
                  <CheckCircle className="w-5 h-5 mr-2" />
                  {loading ? 'Recording...' : 'Confirm Distribution'}
                </Button>

                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => navigate('/check-in')}
                >
                  Cancel
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  if (mode === 'invalid') {
    return (
      <div className="min-h-screen bg-[#FAFAF8] flex items-center justify-center p-4" data-testid="checkin-invalid-page">
        <Card className="w-full max-w-md border-[#E5E5E5]">
          <CardContent className="py-12 text-center">
            <div className="w-16 h-16 rounded-full bg-[#FDECEC] flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-10 h-10 text-[#D34848]" />
            </div>
            <h2 className="text-2xl font-bold text-[#1A1A1A] font-['Manrope'] mb-2">
              QR code no longer valid
            </h2>
            <p className="text-[#595959] mb-6">
              This beneficiary has been removed from the organization.<br />
              The card cannot be used for check-in anymore.
            </p>
            <Button
              className="w-full bg-[#4A5D4E] hover:bg-[#3D4D40]"
              onClick={() => navigate('/check-in')}
              data-testid="back-to-checkin-button"
            >
              Back to Check-in
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (mode === 'success') {
    return (
      <div className="min-h-screen bg-[#FAFAF8] flex items-center justify-center p-4" data-testid="checkin-success-page">
        <Card className="w-full max-w-md border-[#E5E5E5]">
          <CardContent className="py-12 text-center">
            <div className="check-success animate-fade-in">
              <div className="check-icon animate-check">
                <CheckCircle className="w-12 h-12" />
              </div>
              <h2 className="text-2xl font-bold text-[#1A1A1A] font-['Manrope'] mb-2">
                Check-in Completed!
              </h2>
              <p className="text-[#595959] mb-2">
                Distribution recorded for
              </p>
              <p className="text-xl font-semibold text-[#4A5D4E] mb-6">
                {beneficiary?.first_name} {beneficiary?.surname}
              </p>
              <p className="text-sm text-[#8C8C8C] mb-8">
                {formatDateTime(new Date().toISOString(), 'DD/MM/YYYY', '24h')}
              </p>
              <Button
                className="w-full bg-[#4A5D4E] hover:bg-[#3D4D40]"
                onClick={() => navigate('/check-in')}
                data-testid="new-checkin-button"
              >
                New Check-in
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return null;
}
